import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { EffectsContainer } from 'mixins/withEffects';
import './ideationVotePicker.html';
import './ideationVotePicker.scss';

components.preload('vote-bubble');

interface VoteControl {
    value: number
    pickVote: Action
}

export interface IdeationVotePickerVMConfig {
    topicId?: SubscribableOrNullableValue<string>
    initialVote?: SubscribableOrNullableValue<number>
}

@injectable()
export default class IdeationVotePickerVM {
    private initialVote: Subscribable<number | undefined>

    pickedVote: Observable<number | undefined> = ko.observable();
    pickedUpvote = this.pickedVote.mapNotNull(vote => vote > 0 ? vote : undefined);
    pickedDownvote = this.pickedVote.mapNotNull(vote => vote < 0 ? vote : undefined);

    upvoteControls: Subscribable<Array<VoteControl>>
    downvoteControls: Subscribable<Array<VoteControl>>

    hasDownvotes: Subscribable<boolean>

    upvotesShown = ko.observable(false);
    downvotesShown = ko.observable(false);

    mainControlsShown = ko.pureComputed(() => !this.upvotesShown() && !this.downvotesShown());

    constructor(
        @inject(wccModules.componentConfig) config: IdeationVotePickerVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const topicManager = storage.get(TopicManager, { topicId: config.topicId });
        const loadingTopic = topicManager.pluck(t => t.loading, true);
        const topic = topicManager.pluck(m => m.topic);
        const upvotesPerIdea = topic.pluck(t => t.ideationUpvotesMaxPerIdea, 0);
        const downvotesPerIdea = topic.pluck(t => t.ideationDownvotesMaxPerIdea, 0);

        const votesManager = storage.get(IdeationManager, { topicId: config.topicId });
        const votesReady = votesManager.pluck(m => m.isReady, false);
        const availableUpvotes = votesManager.pluck(m => m.availableUpvotes, 0);
        const availableDownvotes = votesManager.pluck(m => m.availableDownvotes, 0);

        const initialVote = ko.flattenComputed(config.initialVote, 0);
        this.initialVote = initialVote.mapSingle(v => v != 0 ? v : undefined);//we replace 0 with undefined to allow value picking
        const initialUpvotes = this.initialVote.mapSingle(v => v != undefined && v > 0 ? v : 0);
        const initialDownvotes = this.initialVote.mapSingle(v => v != undefined && v < 0 ? -v : 0);

        const finalAvailableUpvotes = ko.pureComputed(() => availableUpvotes() + initialUpvotes());
        const finalAvailableDownvotes = ko.pureComputed(() => availableDownvotes() + initialDownvotes());

        const availableUpvotesPerIdea = ko.pureComputed(() => Math.min(upvotesPerIdea(), finalAvailableUpvotes()));
        const availableDownvotesPerIdea = ko.pureComputed(() => Math.min(downvotesPerIdea(), finalAvailableDownvotes()));

        this.upvoteControls = ko.pureComputed(() => this.getUpvoteControls(availableUpvotesPerIdea()));
        this.downvoteControls = ko.pureComputed(() => this.getDownvoteControls(availableDownvotesPerIdea()));

        this.hasDownvotes = availableDownvotesPerIdea.is(v => v > 0);

        const isReady = ko.pureComputed(() => !loadingTopic() && votesReady());

        effects.register([
            this.initialVote.subscribe(() => this.reset())
        ]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'ideation-vote-picker': true
            }
        });
    }

    showUpvotes = () => {
        this.upvotesShown(true);
    }

    showDownvotes = () => {
        this.downvotesShown(true);
    }

    showMainControls = () => {
        this.upvotesShown(false);
        this.downvotesShown(false);
    }

    reset = () => {
        this.showMainControls();
        this.pickedVote(this.initialVote());
    }

    private pickVote(value: number) {
        this.pickedVote(value);
        this.showMainControls();
    }

    private getUpvoteControls(count: number) {
        const values = [...Array(count)].map((v, idx) => idx + 1);
        return this.getVoteControls(values);
    }

    private getDownvoteControls(count: number) {
        const values = [...Array(count)].map((v, idx) => -(idx + 1));
        return this.getVoteControls(values);
    }

    private getVoteControls(values: Array<number>) {
        return values.map(value => (<VoteControl>{
            value: value,
            pickVote: () => this.pickVote(value)
        }));
    }
}